<script setup lang="ts">
import liff from '@line/liff'
import axios from 'axios'
import { reactive, onMounted, watch, ref, computed, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { showDialog, showNotify, showLoadingToast, closeToast, showConfirmDialog } from 'vant'
import 'vant/es/dialog/style'
import 'vant/es/notify/style'
import 'vant/es/toast/style'
import { useAppStore } from '@/stores/AppStore'
import { useTranslation } from 'i18next-vue'
import { format } from 'date-fns'
import router from '@/router'
import { useAppointmentStore } from '@/stores/AppointmentStore'
import { AppointmentBranch, AppointmentEventType, ICreateAppointment } from '@/type/Appointment'
import { useAppointmentRepository } from '@/repositories/appointmentRepo'

// Composables
import { useAppointmentForm } from '@/composables/Appointment/useAppointmentForm'
import { useAppointmentCalendar } from '@/composables/Appointment/useAppointmentCalendar'
import { useAppointmentSelections } from '@/composables/Appointment/useAppointmentSelections'
import { useDepositTypes } from '@/composables/Appointment/useDepositTypes'
import { useAppointmentActions } from '@/composables/Appointment/useAppointmentActions'
import { useInterestTags } from '@/composables/Appointment/useInterestTags'
import { useAppointmentDepositTypeWithInterestTag } from '@/composables/Appointment/useDepositTypeWithInterestTag'
import { buildAppointmentPayload } from '@/composables/Appointment/appointmentPayload'
import { getBranchDisplayNamei18n } from '@/utils/branch'

// Components
import logo from '@/assets/aura_logo.png'
import logoXpress from '@/assets/aura_logo_xpress.png'
import AppointmentBranchDoctorSelector from '@/components/Appointment/AppointmentBranchDoctorSelector.vue'
import AppointmentTimeSlotSelector from '@/components/Appointment/AppointmentTimeSlotSelector.vue'
import AppointmentDepositTypeSelector from '@/components/Appointment/AppointmentDepositTypeSelector.vue'
import AppointmentErrorDialogs from '@/components/Appointment/AppointmentErrorDialogs.vue'
import AppointmentNoteFields from '@/components/Appointment/AppointmentNoteFields.vue'
import AppointmentRecentCard from '@/components/Appointment/AppointmentRecentCard.vue'
import AppointmentInterestTagSelector from '@/components/Appointment/AppointmentInterestTagSelector.vue'
import AppointmentDailyDoctorList from '@/components/Appointment/AppointmentDailyDoctorList.vue'

interface PageData {
  loading: boolean
  clinicId: string
  lineUserId: string
  channelId?: string
  from?: 'course' | 'request'
}

const { t } = useTranslation()
const appStore = useAppStore()
const appointmentStore = useAppointmentStore()
const route = useRoute()
const appointmentRepo = useAppointmentRepository()
const appointmentActions = useAppointmentActions()

if (!appStore.channelId) {
  showNotify({ type: 'danger', message: t('unknown_error') })
  throw new Error('Channel ID is required')
}

const page = reactive<PageData>({
  loading: false,
  clinicId: appStore.clinicUUID,
  lineUserId: appStore.userProfile.userId,
  channelId: appStore.channelId,
  from: route.query.from as PageData['from'],
})

// Use composables
const {
  form,
  disableState,
  mobileValidator,
  emailValidator,
  populateFormFromPatient,
  updateNameByLanguage,
  resetFormKeepPatientData,
} = useAppointmentForm()
const {
  showCalendar,
  dateSelect,
  dateText,
  calendarMinDate,
  calendarMaxDate,
  clinicWorkHour,
  workHourDensityColorLabel,
  setWorkHourDensityColorLabel,
  selectedTime,
  selectedTimeIndex,
  loadingWorkHour,
  formatDate,
  setCalendarDate,
  mappingWorkingHourSlot,
  onTimeSelect,
  formatter: calendarFormatter,
  clearTimeSelection,
  clearDateSelection,
} = useAppointmentCalendar()
const {
  branchText,
  showBranchPicker,
  doctorText,
  showDoctorPicker,
  doctors,
  doctorCalendars,
  doctorCalendarsIn,
  onConfirmBranch: baseOnConfirmBranch,
  onConfirmDoctor: baseOnConfirmDoctor,
  clearDoctorSelection,
  clearBranchSelection,
  updateBranchTextByLanguage,
} = useAppointmentSelections()
const depositTypeState = useDepositTypes()
const {
  depositTypes,
  selectedDepositTypeId,
  initializeDepositTypes,
  onSelectDepositType,
  validateRequireSelectDepositType,
  getSelectedDepositTypeNames,
  resetDepositTypes,
} = depositTypeState
const interestTagState = useInterestTags()
const {
  interestTags,
  isLoadingTags,
  selectedInterestTags,
  onSelectInterestTag,
  getSelectedInterestTagIdsArray,
  getSelectedInterestTagNameArray,
} = interestTagState
// หัตถการตามบริการที่เลือก (LMP-7840) - เปลี่ยนบริการ / ล้างฟอร์มแล้วหัตถการล้างตามเอง
useAppointmentDepositTypeWithInterestTag(depositTypeState, interestTagState)

// ตัวเลือก = หัตถการใต้บริการที่เลือก - ยังไม่เลือก / บริการไม่มีหัตถการ = ซ่อน
// ผูกกับเวลาที่เลือกเหมือนช่องบริการ (เปลี่ยนวัน / สาขาแล้วเวลาถูกล้าง ช่องบริการซ่อน - ช่องหัตถการต้องซ่อนตาม)
const showInterestTagSelector = computed(() => selectedTime.value.length > 0 && interestTags.value.length > 0)

// Local state
const recentCard = ref<InstanceType<typeof AppointmentRecentCard> | null>(null)
const branches = ref<AppointmentBranch[]>([])
const settings = ref<any>({})
const noAppointments = ref<any[]>([])
const limitDialog = ref(false)
const postPoneDialog = ref(false)
const showNumpad = ref(false)
const today = new Date()

// Unique to Appointment.vue: OTP state
const otToken = ref<string | null>(null)
const otp = reactive({
  otpId: '',
  otpCode: '',
  referenceCode: '',
})
const dialogOtp = ref(false)
const showRequestOTPButton = ref(true)
const loadingRequestOTPButton = ref(false)

const isNoPatiant = computed(() => !appStore.patient)
const isAllClinicWorkHourEmpty = computed(() => clinicWorkHour.value.every(workHour => !workHour.active))

// Helper functions
function clearSelect(options: { doctor?: boolean; date?: boolean; time?: boolean }) {
  if (options.doctor) clearDoctorSelection()
  if (options.date) clearDateSelection()
  if (options.time) clearTimeSelection()
}

// Fetch functions with cache support
function fetchAppointmentData() {
  if (appointmentStore.appointmentData) return Promise.resolve(appointmentStore.appointmentData)
  return appointmentRepo.fetchAppointmentData({
    clinicId: page.clinicId,
    lineUserId: page.lineUserId,
    channelId: page.channelId,
  })
}

function fetchBranches() {
  if (appointmentStore.branches.length) return Promise.resolve(appointmentStore.branches)
  return appointmentRepo.fetchBranches({
    clinicId: page.clinicId,
    lineUserId: page.lineUserId,
    channelId: page.channelId,
  })
}

function fetchAppointmentSettings() {
  if (appointmentStore.settings) return Promise.resolve({ data: appointmentStore.settings })
  return appointmentRepo.fetchAppointmentSettings({
    clinicId: page.clinicId,
    lineUserId: page.lineUserId,
    channelId: page.channelId,
  })
}

async function fetchNoAppointment() {
  if (!form.branch) return
  noAppointments.value = await appointmentRepo.fetchNoAppointmentDates({
    branchUuid: form.branch,
    channelId: page.channelId,
  })
}

// บริการที่สนใจ + หัตถการใต้แต่ละบริการของ LINE OA (LMP-7840) - บังคับเลือกบริการ โหลดไม่ได้ = ใช้ฟอร์มไม่ได้
// ปล่อย error ให้ initialize จัดการแบบเดียวกับสาขา / การตั้งค่า (ต้อง deploy backend ก่อน ไม่งั้นหน้านี้ไป NotFound)
// isLoadingTags = สถานะโหลดของช่องหัตถการ - หัตถการมากับรายการบริการ จึงใช้ตัวเดียวกัน
async function fetchServices() {
  try {
    isLoadingTags.value = true
    return await appointmentRepo.fetchServices({
      clinicUuid: appStore.clinicUUID,
      channelId: appStore.channelId,
    })
  } finally {
    isLoadingTags.value = false
  }
}

async function initialize() {
  try {
    page.loading = true
    const [appointmentData, branchList, settingData, services] = await Promise.all([
      fetchAppointmentData(),
      fetchBranches(),
      fetchAppointmentSettings(),
      fetchServices(),
    ])

    // Set fetched data
    settings.value = settingData.data
    appointmentStore.cacheAppointmentSettings(settingData.data)

    branches.value = branchList || []
    appointmentStore.cacheAppointmentBranches(branchList)
    setCalendarDate(settings.value)

    // Check limits
    if (appointmentData.is_limit) {
      limitDialog.value = true
    }
    if (appointmentData.can_postpone === false) {
      postPoneDialog.value = true
    }

    // Populate form from patient data
    if (appointmentData.data) {
      appointmentStore.cacheAppointmentData(appointmentData)
      populateFormFromPatient(appointmentData.data, appStore.language)
    }

    // Auto select when only have one branch
    if (branchList.length === 1 && !form.branch && !branchText.value) {
      onConfirmBranch({
        text: getBranchDisplayNamei18n(branches.value[0], appStore.language),
        uuid: branchList[0].uuid,
      })
    }

    initializeDepositTypes(services)
  } catch (err: unknown) {
    router.replace({ name: 'NotFound' })
    showNotify({ type: 'danger', message: t('unknown_error') })
  } finally {
    page.loading = false
  }
}

async function onConfirmBranch({ text, uuid }: { text: string; uuid: string }) {
  form.branch = uuid
  await baseOnConfirmBranch(
    { text, uuid },
    {
      clearSelect,
      fetchNoAppointment,
      // fetchDoctors: async (branchUuid: string) => {
      //   return await appointmentRepo.fetchDoctors({
      //     clinicId: page.clinicId,
      //     lineUserId: page.lineUserId,
      //     branchUuid,
      //   })
      // },
      // onConfirmDoctor,
    },
    t,
  )
}

async function onConfirmDoctor({ selectedOptions }: { selectedOptions: any[] }) {
  form.doctor = selectedOptions[0].uuid
  await baseOnConfirmDoctor(
    { selectedOptions },
    {
      clearSelect,
      fetchDoctorSchedule: async (doctorUuid: string) => {
        return await appointmentRepo.fetchDoctorWorkSchedule({
          clinicId: page.clinicId,
          lineUserId: page.lineUserId,
          branchUuid: form.branch!,
          doctorUuid,
        })
      },
    },
    settings.value,
  )
}

async function onConfirmCalendar(date: Date) {
  form.date = format(date, 'yyyy-MM-dd')
  showCalendar.value = false
  dateText.value = formatDate(date)
  dateSelect.value = date
  loadingWorkHour.value = true

  const data = await appointmentRepo.fetchTimeSlots({
    clinicId: page.clinicId,
    branchUuid: form.branch!,
    doctorUuid: form.doctor,
    date: form.date,
  })

  setWorkHourDensityColorLabel(data.density_description)

  clinicWorkHour.value = mappingWorkingHourSlot(
    data.working_hour_json_slot,
    date,
    noAppointments.value,
    settings.value,
    doctorCalendars.value,
    form.doctor,
    form.date,
  )
  loadingWorkHour.value = false
}

function formatterWrapper(day: any) {
  return calendarFormatter(day, noAppointments.value, settings.value, doctorCalendarsIn.value, form.doctor, today, t)
}

// OTP functions (unique to Appointment.vue)
async function requestOTP() {
  otp.otpId = ''
  otp.otpCode = ''
  otp.referenceCode = ''

  try {
    loadingRequestOTPButton.value = true
    const url = `${appStore.url}/api/v1/crm/line-app/p/account-link/request-otp/?c=${page.clinicId}`
    const response = await axios.post(url, { mobile: form.mobile })
    showRequestOTPButton.value = false

    otp.otpId = response.data.otpId
    otp.referenceCode = response.data.referenceCode
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      if (err.code === 'ERR_NETWORK') {
        return showNotify({ type: 'danger', message: t('internet_connection_error') })
      } else if (err.response?.status === 400) {
        return showNotify({ type: 'danger', message: err.response.data.message })
      }
    }
    return showNotify({ type: 'danger', message: t('unknown_error') })
  } finally {
    loadingRequestOTPButton.value = false
  }
}

async function sendVerifyAccount(api = 'account-link/verify') {
  const url = `${appStore.url}/api/v1/crm/line-app/p/${api}/`
  return await axios.post(url, {
    c: page.clinicId,
    mobile: form.mobile,
    otpId: otp.otpId,
    otpCode: otp.otpCode,
    channelId: page.channelId,
    accessToken: liff.getAccessToken(),
    ot_token: otToken.value,
  })
}

async function sendVerifyMobile() {
  return await sendVerifyAccount('account-mobile-check')
}

async function onSubmitBookingAndLine(bookingData: any) {
  try {
    page.loading = true
    const response = await sendVerifyMobile()
    page.loading = false

    if (response.status === 200) {
      dialogOtp.value = false
      const message = t('appointment.otp.confirm_identity_message', {
        name: response.data.full_name,
        mobile: response.data.mobile,
      })
      otToken.value = response.data.ot_token

      await showConfirmDialog({
        title: t('appointment.otp.confirm_identity_title'),
        message: message,
        confirmButtonText: t('appointment.yes'),
        cancelButtonText: t('appointment.not_me'),
        theme: 'round-button',
        confirmButtonColor: '#2fc160',
        cancelButtonColor: '#f44336',
      })
        .then(async () => {
          page.loading = true
          const responseLinkAccount = await sendVerifyAccount()

          if (responseLinkAccount.status === 200) {
            try {
              bookingData.name = response.data.full_name
              const success = await appointmentActions.createAppointment(
                bookingData,
                appStore.clinicUUID,
                appStore.userProfile.userId,
                page.channelId,
              )
              if (success) {
                await showSuccessDialog()
                liff.closeWindow()
              }
            } catch (err: unknown) {
              console.log(err)
            }
          }
        })
        .catch((err: unknown) => {
          console.log(err)
          showDialog({
            message: t('appointment.otp.duplicate_patient'),
            theme: 'round-button',
          })
            .then(async () => {
              otToken.value = null
              liff.closeWindow()
            })
            .finally(() => {
              page.loading = false
            })
        })
    }
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 400 && err.response?.data.ot_token) {
        appointmentStore.cacheOtToken(err.response?.data.ot_token)
        dialogOtp.value = false
        router.replace({
          name: 'PatientCreate',
          query: {
            c: appStore.clinicUUID,
            liffId: appStore.liffId,
            clinicType: appStore.clinicType,
            channelId: appStore.channelId,
            mobile: form.mobile,
          },
        })
      } else if (err.response?.status === 400) {
        const errorData = err.response.data
        if (errorData) {
          const message = errorData.message
          if (message === 'Invalid Number') {
            return showNotify({ type: 'danger', message: t('appointment.otp.invalid') })
          }
          return showNotify({ type: 'danger', message })
        } else {
          return showNotify({ type: 'danger', message: t('unknown_error') })
        }
      } else if (err.code === 'ERR_NETWORK') {
        return showNotify({ type: 'danger', message: t('internet_connection_error') })
      } else {
        return showNotify({ type: 'danger', message: t('unknown_error') })
      }
    } else {
      return showNotify({ type: 'danger', message: t('unknown_error') })
    }
  } finally {
    page.loading = false
  }
}

function scrollToTop() {
  const containerName = '.form-container'
  const container = document.querySelector(containerName) as HTMLElement | null

  if (container) {
    // Try smooth scroll first, fallback to instant scroll for mobile compatibility
    container.scrollTo({ top: 0, behavior: 'smooth' })
    // Fallback for browsers that don't support smooth behavior
    setTimeout(() => {
      if (container.scrollTop > 0) {
        container.scrollTop = 0
      }
    }, 100)
  } else {
    // Fallback to window scroll
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setTimeout(() => {
      if (window.scrollY > 0) {
        window.scrollTo(0, 0)
      }
    }, 100)
  }
}

async function onSubmit() {
  if (selectedTime.value.length === 0) {
    return showNotify({ type: 'danger', message: t('appointmentrequest.please_select_time') })
  }
  if (!validateRequireSelectDepositType(t)) return

  try {
    page.loading = true

    const data: ICreateAppointment = buildAppointmentPayload({
      form,
      time: selectedTime.value,
      depositTypeId: selectedDepositTypeId.value,
      depositTypeNames: getSelectedDepositTypeNames(),
      interestTagNames: getSelectedInterestTagNameArray(),
      interestTagIds: getSelectedInterestTagIdsArray(),
    })

    if (isNoPatiant.value) {
      appointmentStore.cacheBookingData(data)
      await requestOTP()
      dialogOtp.value = true
      page.loading = false
      return
    }

    // Create appointment
    const success = await appointmentActions.createAppointment(
      data,
      appStore.clinicUUID,
      appStore.userProfile.userId,
      page.channelId,
    )
    if (success) {
      // post action after create appointment success
      page.loading = false
      recentCard.value?.getLimitedRecentAppointments()
      await showSuccessDialog()
      resetAllFormData()
      scrollToTop()
    }
  } catch (err: unknown) {
    console.error(err)
  } finally {
    page.loading = false
  }
}

async function showSuccessDialog() {
  if (!dateSelect.value) return
  const formatdate = format(dateSelect.value, 'dd/MM/yyyy')

  return showDialog({
    title: t('appointment.success_title'),
    message: t('appointment.success_message', {
      branch: branchText.value,
      date: formatdate,
      time: selectedTime.value[0].data.start,
    }),
    confirmButtonText: t('close_window'),
    confirmButtonColor: '#2fc160',
    theme: 'round-button',
  })
}

function resetAllFormData() {
  resetFormKeepPatientData()
  resetDepositTypes()
  clearBranchSelection()
  clearDateSelection()
  clearTimeSelection()
  clearDoctorSelection()
}

const onCloseLimitDialog = () => {
  limitDialog.value = false
  liff.closeWindow()
}

const onClosePostPoneDialog = () => {
  postPoneDialog.value = false
  liff.closeWindow()
}

onMounted(async () => {
  if (!page.clinicId || !page.lineUserId) {
    console.error('Missing clinicId or lineUserId')
    return
  }

  appointmentRepo.createAppointmentTrickingEvent(AppointmentEventType.VISIT_APPOINTMENT)
  await initialize()
})

onBeforeUnmount(() => {
  // Reset local state
  branches.value = []
  settings.value = {}
  noAppointments.value = []
  showNumpad.value = false
  // Note: Composables handle their own cleanup
})

watch(showCalendar, val => {
  if (val) clearTimeSelection()
})

watch(
  () => page.loading,
  loading => {
    if (loading) {
      showLoadingToast({
        forbidClick: true,
        duration: 0,
        loadingType: 'spinner',
      })
    } else {
      closeToast()
    }
  },
)

watch(
  () => appStore.language,
  () => {
    updateNameByLanguage(appStore.language)
    updateBranchTextByLanguage(branches.value, form.branch, appStore.language)
  },
)
</script>

<template>
  <div style="background-color: white">
    <div class="tw-bg-primary2 tw-flex tw-items-center tw-justify-between tw-pr-4">
      <img v-if="appStore.isXpress" :src="logoXpress" class="tw-h-[75px]" />
      <img v-else :src="logo" class="tw-h-[75px]" />
      <LanguageButton />
    </div>

    <div class="tw-px-4 form-container" :class="{ 'have-footer': !isNoPatiant }">
      <!-- Recent Appointments Card (Unique to Appointment.vue) -->
      <div class="tw-mt-2" v-if="!isNoPatiant">
        <AppointmentRecentCard ref="recentCard" />
      </div>

      <van-divider class="tw-my-2" />

      <h2 class="tw-text-[#2f4b76] tw-text-2xl tw-font-bold tw-mb-2">{{ t('appointment.create_new') }}</h2>

      <van-form @submit="onSubmit">
        <!-- Form Fields Component -->
        <van-cell-group class="group-container" inset>
          <van-field
            v-if="!isNoPatiant"
            style="padding-top: 10px; padding-bottom: 10px"
            v-model="form.name"
            :required="false"
            :label="t('appointmentrequest.name')"
            :placeholder="$t('appointmentrequest.name_placeholder')"
            :disabled="disableState.disableName || !isNoPatiant"
            :rules="[{ required: true, message: t('appointmentrequest.name_require') }]"
          />
          <van-field
            v-model="form.mobile"
            :required="!disableState.disableMobile"
            :label="t('appointmentrequest.phone_number')"
            :placeholder="$t('appointmentrequest.phone_number_placeholder')"
            :disabled="disableState.disableMobile || !isNoPatiant"
            readonly
            clickable
            @touchstart.stop="disableState.disableMobile ? (showNumpad = false) : (showNumpad = true)"
            :rules="[
              {
                required: true,
                message: t('appointmentrequest.phone_number_require'),
              },
              {
                validator: mobileValidator,
                message: t('appointmentrequest.phone_number_require'),
              },
            ]"
          />
          <van-field
            v-if="isNoPatiant || !appStore.patient?.email"
            v-model="form.email"
            type="email"
            :label="t('appointmentrequest.email')"
            :disabled="disableState.disableEmail"
            :placeholder="t('appointmentrequest.email_placeholder')"
            :rules="[
              {
                validator: emailValidator,
                message: t('appointmentrequest.email_validate'),
              },
            ]"
            maxlength="255"
          />
          <van-number-keyboard v-model="form.mobile" :show="showNumpad" :maxlength="10" @blur="showNumpad = false" />
        </van-cell-group>

        <!-- Branch/Doctor/Date Selector Component -->
        <AppointmentBranchDoctorSelector
          :branch-text="branchText"
          :show-branch-picker="showBranchPicker"
          :branches="branches"
          :doctor-text="doctorText"
          :show-doctor-picker="showDoctorPicker"
          :doctors="doctors"
          :hiding-doctor="settings.appoint_liff_hiding_doctor"
          :has-branch="!!form.branch"
          :date-text="dateText"
          :show-calendar="showCalendar"
          :calendar-min-date="calendarMinDate"
          :calendar-max-date="calendarMaxDate"
          :date-select="dateSelect"
          :formatter="formatterWrapper"
          @update:show-branch-picker="showBranchPicker = $event"
          @update:show-doctor-picker="showDoctorPicker = $event"
          @update:show-calendar="showCalendar = $event"
          @confirm-branch="onConfirmBranch"
          @confirm-doctor="onConfirmDoctor"
          @confirm-calendar="onConfirmCalendar"
        />

        <!-- Time Slot Selector Component -->
        <AppointmentTimeSlotSelector
          :has-date="!!form.date"
          :loading="loadingWorkHour"
          :time-slots="clinicWorkHour"
          :selected-time-index="selectedTimeIndex"
          :density-color-label="workHourDensityColorLabel"
          @select-time="onTimeSelect"
        />

        <div v-if="isAllClinicWorkHourEmpty && form.date" class="tw-text-center tw-py-2 tw-text-sm tw-text-gray-500">
          {{ t('appointment.slot_full') }}
        </div>

        <!-- Deposit Type Selector Component -->
        <AppointmentDepositTypeSelector
          :show="selectedTime.length > 0"
          :deposit-types="depositTypes"
          :selected-deposit-type-id="selectedDepositTypeId"
          @select-type="onSelectDepositType"
        />
        <!-- Channel Tag Component -->
        <AppointmentInterestTagSelector
          :show="showInterestTagSelector"
          :selected-interest-tags="selectedInterestTags"
          :interest-tags="interestTags"
          :loading="isLoadingTags"
          @select-tag="onSelectInterestTag"
        />

        <!-- Note Field -->
        <AppointmentNoteFields v-model="form.note" :helper-text="settings.appoint_liff_helper_msg" />

        <!-- Doctor Work Schedule -->
        <AppointmentDailyDoctorList :branch-uuid="form.branch" :date="dateSelect" />

        <!-- Submit Section -->
        <div style="padding-top: 20px; padding-bottom: 10px">
          <van-notice-bar
            style="margin-top: 16px"
            wrapable
            :scrollable="false"
            :text="`${t('appointmentrequest.note')}: ${settings.appoint_liff_warning_msg || ''}`"
            left-icon="volume-o"
          />
          <div style="margin: 16px">
            <van-button round block type="primary" native-type="submit" style="color: white">
              {{ t('appointmentrequest.submit') }}
            </van-button>
          </div>
        </div>
      </van-form>
    </div>

    <!-- Dialogs Component -->
    <AppointmentErrorDialogs
      :show-limit="limitDialog"
      :show-postpone="postPoneDialog"
      @close-limit="onCloseLimitDialog"
      @close-postpone="onClosePostPoneDialog"
    />

    <!-- OTP Dialog (Unique to Appointment.vue) -->
    <van-dialog
      :show="dialogOtp"
      :show-confirm-button="true"
      :show-cancel-button="true"
      style="text-align: center"
      @confirm="onSubmitBookingAndLine(appointmentStore.bookingData)"
      @cancel="dialogOtp = false"
    >
      <div style="padding: 20px">
        <p class="title">{{ t('appointment.otp.enter') }}</p>
        <van-field
          class="tw-mt-2"
          style="border: 1px solid #c8c9cc; border-radius: 10px"
          v-model="otp.otpCode"
          type="digit"
          maxlength="6"
          :placeholder="t('appointment.otp.enter')"
        >
          <template #button>
            <van-button
              v-if="showRequestOTPButton"
              :loading="loadingRequestOTPButton"
              size="small"
              type="primary"
              native-type="button"
              @click="requestOTP"
              style="color: white"
              >{{ t('appointment.otp.resend') }}</van-button
            >
            <van-button v-else disabled size="small" type="primary">
              <van-count-down
                :time="60 * 3000"
                format="mm:ss"
                @finish="
                  () => {
                    showRequestOTPButton = true
                    loadingRequestOTPButton = false
                  }
                "
              />
            </van-button>
          </template>
        </van-field>
        <p class="tw-mt-2 tw-my-1">{{ t('appointment.otp.reference', { code: otp.referenceCode }) }}</p>
      </div>
    </van-dialog>
  </div>
</template>

<style>
@import 'vue-select/dist/vue-select.css';
.group-container {
  margin: 15px 0px;
  border: 2px solid #c8c9cc;
  border-radius: 20px;
}

.icon-button {
  background-color: var(--van-button-primary-background);
  color: white;
  border-radius: 100%;
}

.title {
  font-size: 24px;
  font-weight: bold;
}

.sub-text {
  font-size: 14px;
  margin: 12px 0px;
}

.form-container {
  height: calc(100dvh - 75px);
  overflow-y: auto;
}

.have-footer {
  height: calc(100dvh - 145px);
}

:deep(textarea) {
  height: 0px;
}
</style>
